<template>
  <div class="gl-form-page">
    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-form-header">
        <div>
          <div class="gl-form-breadcrumb">
            <router-link to="/">Home</router-link>
            <span>/</span>
            <router-link to="/owner/staff">Staff</router-link>
            <span>/</span>
            <span>{{ isEdit ? 'Edit' : 'Create' }}</span>
          </div>
          <h1 class="gl-form-title">{{ isEdit ? 'Edit Staff' : 'Add Staff' }}</h1>
        </div>
      </div>
    </div>

    <div class="container-fluid">
      <!-- Loading -->
      <div v-if="loading" class="gl-loading">
        <div class="spinner-border" role="status" style="color:#900C27;width:28px;height:28px;border-width:3px"></div>
        <span>Loading staff…</span>
      </div>

      <div v-else class="gl-staff-layout">
        <!-- LEFT: Form -->
        <form @submit.prevent="submitForm" class="gl-form-body">

          <!-- Live preview strip -->
          <div class="gl-preview-strip" v-if="form.name">
            <div class="gl-preview-avatar">{{ form.name.charAt(0).toUpperCase() }}{{ (form.name.split(' ')[1] || '').charAt(0).toUpperCase() }}</div>
            <div class="gl-preview-info">
              <div class="gl-preview-name">{{ form.name }}</div>
              <div class="gl-preview-meta">
                <span v-if="form.email" class="gl-preview-tag">{{ form.email }}</span>
                <span v-if="form.role" :class="['gl-preview-role', form.role]">{{ roleLabel(form.role) }}</span>
              </div>
            </div>
            <div v-if="isEdit" :class="['gl-preview-status', form.is_active ? 'active' : 'inactive']">
              <span class="gl-preview-dot"></span>{{ form.is_active ? 'Active' : 'Inactive' }}
            </div>
          </div>

          <!-- ── Section 1: Staff Information ── -->
          <div class="gl-section" style="animation-delay:0ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Staff Information</div>
                <div class="gl-section-desc">Name, contact details, and role assignment</div>
              </div>
            </div>

            <div class="gl-fields">
              <div class="gl-field-row">
                <div class="gl-field">
                  <label class="gl-label">Full Name <span class="gl-req">*</span></label>
                  <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.name }" v-model="form.name" placeholder="e.g. Ahmad Faris" required>
                  <div v-if="errors.name" class="gl-err">{{ errors.name[0] }}</div>
                </div>
                <div class="gl-field">
                  <label class="gl-label">Email <span class="gl-req">*</span></label>
                  <input type="email" class="gl-input" :class="{ 'gl-input-err': errors.email }" v-model="form.email" placeholder="staff@example.com" required>
                  <div v-if="errors.email" class="gl-err">{{ errors.email[0] }}</div>
                </div>
              </div>
              <div class="gl-field-row">
                <div class="gl-field">
                  <label class="gl-label">Phone <span class="gl-optional">(optional)</span></label>
                  <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.phone }" v-model="form.phone" placeholder="+60123456789">
                  <div v-if="errors.phone" class="gl-err">{{ errors.phone[0] }}</div>
                </div>
                <div class="gl-field">
                  <label class="gl-label">Role <span class="gl-req">*</span></label>
                  <select class="gl-input gl-select" :class="{ 'gl-input-err': errors.role }" v-model="form.role" required>
                    <option value="">Select Role</option>
                    <option value="outlet-manager">Outlet Manager</option>
                    <option value="cashier">Cashier</option>
                  </select>
                  <div v-if="errors.role" class="gl-err">{{ errors.role[0] }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Section 2: Branch Assignment ── -->
          <div class="gl-section" style="animation-delay:60ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Assign to Branches <span class="gl-req">*</span></div>
                <div class="gl-section-desc">Select one or more branches where this staff member will work</div>
              </div>
            </div>

            <!-- Loading branches -->
            <div v-if="loadingBranches" class="gl-branches-loading">
              <div class="spinner-border spinner-border-sm" style="color:#900C27;border-width:2px"></div>
              <span>Loading branches…</span>
            </div>

            <!-- No branches warning -->
            <div v-else-if="branches.length === 0" class="gl-no-branches">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              No branches found. <router-link to="/owner/branches/create">Create a branch first</router-link>
            </div>

            <!-- Branch cards -->
            <div v-else class="gl-branch-cards">
              <button
                v-for="branch in branches"
                :key="branch.id"
                type="button"
                :class="['gl-branch-card', { active: form.outlet_ids.includes(branch.id) }]"
                @click="toggleBranch(branch.id)"
              >
                <div class="gl-bcard-avatar">{{ branch.name.charAt(0).toUpperCase() }}{{ (branch.name.split(' ')[1] || '').charAt(0).toUpperCase() }}</div>
                <div class="gl-bcard-info">
                  <div class="gl-bcard-name">{{ branch.name }}</div>
                  <div class="gl-bcard-loc">{{ branch.city }}, {{ branch.state }}</div>
                </div>
                <div class="gl-bcard-check">
                  <svg v-if="form.outlet_ids.includes(branch.id)" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              </button>
            </div>

            <div v-if="errors.outlet_ids" class="gl-err" style="margin-top:8px">{{ errors.outlet_ids[0] }}</div>
          </div>

          <!-- ── Section 3: Password ── -->
          <div class="gl-section" style="animation-delay:120ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <div>
                <div class="gl-section-title">{{ isEdit ? 'Change Password' : 'Password' }}</div>
                <div class="gl-section-desc">{{ isEdit ? 'Leave blank to keep the current password' : 'Minimum 8 characters' }}</div>
              </div>
            </div>

            <div class="gl-fields">
              <div class="gl-field-row">
                <div class="gl-field">
                  <label class="gl-label">Password <span v-if="!isEdit" class="gl-req">*</span></label>
                  <div class="gl-pw-wrap">
                    <input :type="showPw ? 'text' : 'password'" class="gl-input gl-input-pw" :class="{ 'gl-input-err': errors.password }" v-model="form.password" placeholder="••••••••" :required="!isEdit">
                    <button type="button" class="gl-pw-toggle" @click="showPw = !showPw" tabindex="-1">
                      <svg v-if="!showPw" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    </button>
                  </div>
                  <!-- Password strength bar -->
                  <div v-if="form.password" class="gl-pw-strength">
                    <div class="gl-pw-bars">
                      <div :class="['gl-pw-bar', pwStrength >= 1 ? 'filled' : '']"></div>
                      <div :class="['gl-pw-bar', pwStrength >= 2 ? 'filled' : '']"></div>
                      <div :class="['gl-pw-bar', pwStrength >= 3 ? 'filled' : '']"></div>
                      <div :class="['gl-pw-bar', pwStrength >= 4 ? 'filled' : '']"></div>
                    </div>
                    <span :class="['gl-pw-label', pwStrengthClass]">{{ pwStrengthLabel }}</span>
                  </div>
                  <div v-if="errors.password" class="gl-err">{{ errors.password[0] }}</div>
                </div>
                <div class="gl-field">
                  <label class="gl-label">Confirm Password</label>
                  <div class="gl-pw-wrap">
                    <input :type="showPwConfirm ? 'text' : 'password'" class="gl-input gl-input-pw" :class="{ 'gl-input-err': passwordMismatch }" v-model="form.password_confirmation" placeholder="••••••••">
                    <button type="button" class="gl-pw-toggle" @click="showPwConfirm = !showPwConfirm" tabindex="-1">
                      <svg v-if="!showPwConfirm" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    </button>
                  </div>
                  <div v-if="passwordMismatch" class="gl-err">Passwords do not match</div>
                  <div v-else-if="form.password_confirmation && !passwordMismatch" class="gl-match">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Passwords match
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Section 4: Status (edit only) ── -->
          <div v-if="isEdit" class="gl-section gl-section-inline" style="animation-delay:180ms">
            <div class="gl-section-header" style="margin-bottom:0">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div style="flex:1">
                <div class="gl-section-title">Staff Status</div>
                <div class="gl-section-desc">{{ form.is_active ? 'Staff member is active and can log in' : 'Staff member is deactivated' }}</div>
              </div>
              <button type="button" :class="['gl-toggle', { on: form.is_active }]" @click="form.is_active = !form.is_active">
                <span class="gl-toggle-knob"></span>
              </button>
            </div>
          </div>

          <!-- ── Actions ── -->
          <div class="gl-form-actions" style="animation-delay:220ms">
            <router-link to="/owner/staff" class="gl-btn-cancel">Cancel</router-link>
            <button
              type="submit"
              class="gl-btn-submit"
              :disabled="submitting || passwordMismatch || (!isEdit && form.outlet_ids.length === 0)"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              {{ isEdit ? 'Save Changes' : 'Add Staff' }}
            </button>
          </div>

          <!-- ── Sticky action bar (mobile only) ── -->
          <div class="gl-sticky-bar">
            <router-link to="/owner/staff" class="gl-btn-cancel gl-sticky-cancel">Cancel</router-link>
            <button
              type="submit"
              class="gl-btn-submit gl-sticky-submit"
              :disabled="submitting || passwordMismatch || (!isEdit && form.outlet_ids.length === 0)"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              {{ isEdit ? 'Save Changes' : 'Add Staff' }}
            </button>
          </div>
        </form>

        <!-- RIGHT: Sidebar -->
        <div class="gl-staff-sidebar">

          <!-- Role Permissions Card -->
          <div class="gl-sidebar-card" style="animation-delay:80ms">
            <div class="gl-sidebar-card-header">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Role Permissions
            </div>

            <div v-if="!form.role" class="gl-perm-empty">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" class="gl-perm-empty-icon"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span>Select a role to see its permissions</span>
            </div>

            <div v-else>
              <div class="gl-role-badge" :class="form.role">
                <svg v-if="form.role === 'outlet-manager'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>
                <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {{ roleLabel(form.role) }}
              </div>

              <ul class="gl-perm-list">
                <template v-if="form.role === 'outlet-manager'">
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> View &amp; manage orders</li>
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> Update order status</li>
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> View products &amp; categories</li>
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> Access dashboard</li>
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> View reports</li>
                </template>
                <template v-else-if="form.role === 'cashier'">
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> View orders</li>
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> Update order status</li>
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> View products</li>
                  <li><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg> Basic dashboard access</li>
                  <li class="gl-perm-no"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Reports access</li>
                </template>
              </ul>
            </div>
          </div>

          <!-- Selected Branches Summary -->
          <div v-if="form.outlet_ids.length > 0" class="gl-sidebar-card" style="animation-delay:140ms">
            <div class="gl-sidebar-card-header">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              Assigned Branches
              <span class="gl-sidebar-count">{{ form.outlet_ids.length }}</span>
            </div>
            <div class="gl-assigned-list">
              <div v-for="branchId in form.outlet_ids" :key="branchId" class="gl-assigned-item">
                <div class="gl-assigned-dot"></div>
                <span>{{ getBranchName(branchId) }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Upgrade Modal -->
    <UpgradeModal
      ref="upgradeModal"
      modal-id="staffUpgradeModal"
      title="Staff Limit Reached"
      :message="upgradeMessage"
      :current-plan="currentPlan"
      resource-label="Staff"
      :used="usageUsed"
      :limit="usageLimit"
    />
  </div>
</template>

<script>
import { ownerStaffApi, ownerBranchApi } from '@/services/ownerApi';
import { subscriptionApi } from '@/services/subscriptionApi';
import UpgradeModal from '@/components/owner/common/UpgradeModal.vue';

export default {
  name: 'StaffForm',
  components: { UpgradeModal },

  data() {
    return {
      loading: false,
      loadingBranches: false,
      submitting: false,
      isEdit: false,
      showPw: false,
      showPwConfirm: false,
      errors: {},
      branches: [],
      upgradeMessage: '',
      currentPlan: '',
      usageUsed: 0,
      usageLimit: 0,
      form: {
        name: '',
        email: '',
        phone: '',
        role: '',
        password: '',
        password_confirmation: '',
        is_active: true,
        outlet_ids: []
      }
    };
  },

  computed: {
    passwordMismatch() {
      if (!this.form.password && !this.form.password_confirmation) return false;
      return this.form.password !== this.form.password_confirmation;
    },
    pwStrength() {
      const p = this.form.password;
      if (!p) return 0;
      let score = 0;
      if (p.length >= 8) score++;
      if (/[A-Z]/.test(p)) score++;
      if (/[0-9]/.test(p)) score++;
      if (/[^A-Za-z0-9]/.test(p)) score++;
      return score;
    },
    pwStrengthLabel() {
      return ['', 'Weak', 'Fair', 'Good', 'Strong'][this.pwStrength] || '';
    },
    pwStrengthClass() {
      return ['', 'weak', 'fair', 'good', 'strong'][this.pwStrength] || '';
    }
  },

  mounted() {
    this.fetchBranches();
    if (this.$route.params.id) {
      this.isEdit = true;
      this.fetchStaff();
    }
  },

  methods: {
    roleLabel(role) {
      return role === 'outlet-manager' ? 'Outlet Manager' : role === 'cashier' ? 'Cashier' : role;
    },

    toggleBranch(id) {
      const idx = this.form.outlet_ids.indexOf(id);
      if (idx === -1) this.form.outlet_ids.push(id);
      else this.form.outlet_ids.splice(idx, 1);
    },

    async fetchBranches() {
      try {
        this.loadingBranches = true;
        const response = await ownerBranchApi.list({ per_page: 100 });
        this.branches = response.data.data;
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      } finally {
        this.loadingBranches = false;
      }
    },

    getBranchName(branchId) {
      const branch = this.branches.find(b => b.id === branchId);
      return branch ? branch.name : 'Unknown Branch';
    },

    async fetchStaff() {
      try {
        this.loading = true;
        const response = await ownerStaffApi.get(this.$route.params.id);
        const staff = response.data.data;
        const outletIds = [];
        if (staff.outlet_id) outletIds.push(staff.outlet_id);
        if (staff.assigned_outlets) {
          staff.assigned_outlets.forEach(o => { if (!outletIds.includes(o.id)) outletIds.push(o.id); });
        }
        this.form = {
          name: staff.name, email: staff.email, phone: staff.phone || '',
          role: staff.roles?.[0] || '',
          password: '', password_confirmation: '',
          is_active: staff.is_active, outlet_ids: outletIds
        };
      } catch (error) {
        console.error('Failed to fetch staff:', error);
        this.$router.push('/owner/staff');
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      if (this.passwordMismatch) return;
      if (this.form.outlet_ids.length === 0) {
        this.errors = { outlet_ids: ['Please select at least one branch'] };
        return;
      }
      try {
        this.submitting = true;
        this.errors = {};
        const data = { ...this.form };
        delete data.password_confirmation;
        if (this.isEdit && !data.password) delete data.password;
        if (this.isEdit) {
          await ownerStaffApi.update(this.$route.params.id, data);
          await ownerStaffApi.assign(this.$route.params.id, data.outlet_ids);
        } else {
          await ownerStaffApi.create(data);
        }
        this.$router.push('/owner/staff');
      } catch (error) {
        console.error('Failed to save staff:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          const message = error.response?.data?.message || 'Failed to save staff';
          if (this.isPlanLimitError(message)) this.showUpgradeModal(message);
          else alert(message);
        }
      } finally {
        this.submitting = false;
      }
    },

    isPlanLimitError(message) {
      return ['limit', 'maximum', 'exceeded', 'upgrade', 'plan'].some(k => message.toLowerCase().includes(k));
    },

    async showUpgradeModal(message) {
      this.upgradeMessage = message;
      try {
        const response = await subscriptionApi.getUsage();
        const data = response.data.data;
        this.currentPlan = data.plan?.name || 'Free';
        this.usageUsed = data.limits?.staff?.used || 0;
        this.usageLimit = data.limits?.staff?.limit || 0;
      } catch (e) {
        console.error('Failed to fetch usage:', e);
      }
      this.$refs.upgradeModal?.show();
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-form-page { padding-bottom: 48px; }

.gl-form-header {
  display: flex; align-items: flex-end;
  padding: 28px 0 20px;
}
.gl-form-breadcrumb {
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; color: #9ca3af; margin-bottom: 4px;
}
.gl-form-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-form-breadcrumb a:hover { color: #900C27; }
.gl-form-title {
  font-size: 22px; font-weight: 700; color: #111318;
  letter-spacing: -0.4px; margin: 0;
}

.gl-loading {
  display: flex; align-items: center; gap: 12px;
  padding: 60px 0; justify-content: center; font-size: 14px; color: #9ca3af;
}

/* ── Two-col layout ── */
.gl-staff-layout {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 16px;
  align-items: start;
}

/* ── Form body ── */
.gl-form-body { display: flex; flex-direction: column; gap: 12px; }

/* ── Preview strip ── */
.gl-preview-strip {
  display: flex; align-items: center; gap: 12px;
  background: #fff; border: 1.5px solid rgba(144,12,39,0.2);
  border-radius: 14px; padding: 12px 16px;
  animation: gl-fade-in 0.25s ease;
}
@keyframes gl-fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-preview-avatar {
  width: 38px; height: 38px; border-radius: 10px;
  background: linear-gradient(135deg, #900C27, #6f0a1e);
  color: #fff; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; letter-spacing: 0.3px;
}
.gl-preview-info { flex: 1; min-width: 0; }
.gl-preview-name { font-size: 14px; font-weight: 700; color: #111318; }
.gl-preview-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 3px; }
.gl-preview-tag {
  font-size: 11px; background: #f3f4f6; color: #6b7280;
  padding: 2px 8px; border-radius: 20px; font-weight: 500;
}
.gl-preview-role {
  font-size: 11px; padding: 2px 8px; border-radius: 20px; font-weight: 600;
}
.gl-preview-role.outlet-manager { background: rgba(144,12,39,0.10); color: #900C27; }
.gl-preview-role.cashier { background: rgba(59,130,246,0.10); color: #3b82f6; }
.gl-preview-status {
  display: flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 600; padding: 4px 10px;
  border-radius: 20px; flex-shrink: 0;
}
.gl-preview-status.active { background: rgba(144,12,39,0.10); color: #900C27; }
.gl-preview-status.inactive { background: #f3f4f6; color: #9ca3af; }
.gl-preview-dot {
  width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0;
}
.gl-preview-status.active .gl-preview-dot {
  animation: gl-pulse 2s ease infinite;
}
@keyframes gl-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(144,12,39,0.4); }
  70%  { box-shadow: 0 0 0 5px rgba(144,12,39,0); }
  100% { box-shadow: 0 0 0 0 rgba(144,12,39,0); }
}

/* ── Sections ── */
.gl-section {
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 16px; padding: 22px;
  animation: gl-sect-in 0.3s ease both;
}
@keyframes gl-sect-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-section-inline { padding: 18px 22px; }
.gl-section-header {
  display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
}
.gl-section-icon {
  width: 34px; height: 34px; border-radius: 9px;
  background: rgba(144,12,39,0.10); color: #900C27;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-section-title { font-size: 14px; font-weight: 700; color: #111318; }
.gl-section-desc { font-size: 12px; color: #9ca3af; margin-top: 1px; }

/* ── Fields ── */
.gl-fields { display: flex; flex-direction: column; gap: 16px; }
.gl-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.gl-field { display: flex; flex-direction: column; gap: 5px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #374151; }
.gl-req { color: #ef4444; margin-left: 1px; }
.gl-optional { color: #9ca3af; font-weight: 400; margin-left: 2px; }
.gl-err { font-size: 11.5px; color: #ef4444; }
.gl-match { font-size: 11.5px; color: #900C27; display: flex; align-items: center; gap: 4px; }

.gl-input {
  height: 40px; border: 1.5px solid #e5e7eb; border-radius: 10px;
  padding: 0 12px; font-size: 13.5px; color: #111318;
  background: #fafafa; outline: none; width: 100%;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.gl-input:focus {
  border-color: #900C27; background: #fff;
  box-shadow: 0 0 0 3px rgba(144,12,39,0.10);
}
.gl-input::placeholder { color: #c4c8d0; }
.gl-input-err { border-color: #ef4444 !important; }
.gl-input-err:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.10) !important; }
.gl-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center;
  padding-right: 36px; cursor: pointer;
}

/* ── Password ── */
.gl-pw-wrap { position: relative; }
.gl-input-pw { padding-right: 40px; }
.gl-pw-toggle {
  position: absolute; right: 11px; top: 50%; transform: translateY(-50%);
  border: none; background: transparent; color: #9ca3af;
  cursor: pointer; display: flex; align-items: center; padding: 2px;
}
.gl-pw-toggle:hover { color: #374151; }
.gl-pw-strength { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
.gl-pw-bars { display: flex; gap: 3px; }
.gl-pw-bar {
  width: 32px; height: 3px; border-radius: 2px;
  background: #e5e7eb; transition: background 0.2s;
}
.gl-pw-bar.filled { background: #900C27; }
.gl-pw-label { font-size: 11px; font-weight: 600; }
.gl-pw-label.weak { color: #ef4444; }
.gl-pw-label.fair { color: #f59e0b; }
.gl-pw-label.good { color: #3b82f6; }
.gl-pw-label.strong { color: #900C27; }

/* ── Branch cards ── */
.gl-branches-loading {
  display: flex; align-items: center; gap: 8px;
  color: #6b7280; font-size: 13px; padding: 12px 0;
}
.gl-no-branches {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #b45309;
  background: #fffbeb; border: 1px solid #fde68a;
  border-radius: 10px; padding: 12px 14px;
}
.gl-no-branches a { color: #900C27; font-weight: 600; }

.gl-branch-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; }
.gl-branch-card {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; border-radius: 12px;
  border: 1.5px solid #e5e7eb; background: #fafafa;
  cursor: pointer; text-align: left; transition: all 0.15s;
  position: relative;
}
.gl-branch-card:hover { border-color: #900C27; background: rgba(144,12,39,0.04); }
.gl-branch-card.active { border-color: #900C27; background: rgba(144,12,39,0.07); }
.gl-bcard-avatar {
  width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
  background: linear-gradient(135deg, #900C27, #6f0a1e);
  color: #fff; font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.gl-branch-card:not(.active) .gl-bcard-avatar { background: #e5e7eb; color: #6b7280; }
.gl-bcard-info { flex: 1; min-width: 0; }
.gl-bcard-name { font-size: 13px; font-weight: 600; color: #111318; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gl-branch-card.active .gl-bcard-name { color: #900C27; }
.gl-bcard-loc { font-size: 11px; color: #9ca3af; margin-top: 1px; }
.gl-bcard-check {
  width: 18px; height: 18px; border-radius: 50%;
  background: #900C27; display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0; opacity: 0; transition: opacity 0.15s;
}
.gl-branch-card.active .gl-bcard-check { opacity: 1; }

/* ── Status toggle ── */
.gl-toggle {
  width: 44px; height: 24px; border-radius: 12px;
  border: none; cursor: pointer; padding: 0;
  background: #e5e7eb; position: relative;
  transition: background 0.2s; flex-shrink: 0;
}
.gl-toggle.on { background: #900C27; }
.gl-toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fff; transition: transform 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2); display: block;
}
.gl-toggle.on .gl-toggle-knob { transform: translateX(20px); }

/* ── Actions ── */
.gl-form-actions {
  display: flex; align-items: center; gap: 10px;
  animation: gl-sect-in 0.3s ease both;
}
.gl-btn-cancel {
  padding: 10px 20px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600;
  text-decoration: none; transition: all 0.15s;
}
.gl-btn-cancel:hover { border-color: #d1d5db; background: #f9fafb; color: #374151; }
.gl-btn-submit {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 10px; border: none;
  background: #900C27; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-btn-submit:hover:not(:disabled) {
  background: #6f0a1e; transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(144,12,39,0.30);
}
.gl-btn-submit:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Sidebar ── */
.gl-staff-sidebar { display: flex; flex-direction: column; gap: 12px; }

.gl-sidebar-card {
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 16px; padding: 18px;
  animation: gl-sect-in 0.3s ease both;
}
.gl-sidebar-card-header {
  display: flex; align-items: center; gap: 7px;
  font-size: 12.5px; font-weight: 700; color: #374151;
  margin-bottom: 14px; letter-spacing: 0.1px;
}
.gl-sidebar-card-header svg { color: #9ca3af; }
.gl-sidebar-count {
  margin-left: auto; background: rgba(144,12,39,0.10);
  color: #900C27; font-size: 11px; font-weight: 700;
  padding: 1px 8px; border-radius: 20px;
}

.gl-perm-empty {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; text-align: center; padding: 16px 8px;
  color: #9ca3af; font-size: 12.5px;
}
.gl-perm-empty-icon { color: #d1d5db; }

.gl-role-badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 600; padding: 4px 10px;
  border-radius: 20px; margin-bottom: 12px;
}
.gl-role-badge.outlet-manager { background: rgba(144,12,39,0.10); color: #900C27; }
.gl-role-badge.cashier { background: rgba(59,130,246,0.10); color: #3b82f6; }

.gl-perm-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 7px; }
.gl-perm-list li {
  display: flex; align-items: center; gap: 7px;
  font-size: 12.5px; color: #374151;
}
.gl-perm-list li svg { color: #900C27; flex-shrink: 0; }
.gl-perm-no { color: #9ca3af !important; }
.gl-perm-no svg { color: #d1d5db !important; }

.gl-assigned-list { display: flex; flex-direction: column; gap: 8px; }
.gl-assigned-item {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px; color: #374151; font-weight: 500;
}
.gl-assigned-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #900C27; flex-shrink: 0;
}

/* ── Sticky action bar ── */
.gl-sticky-bar {
  display: none;
  position: fixed; bottom: 0; left: 0; right: 0; z-index: 100;
  background: #fff; border-top: 1.5px solid #f0f1f3;
  padding: 12px 16px; gap: 10px;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.06);
}
.gl-sticky-cancel {
  flex: 0 0 auto; padding: 11px 18px !important;
}
.gl-sticky-submit {
  flex: 1; justify-content: center;
  padding: 11px 18px !important; font-size: 14px !important;
}

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-form-page { padding-bottom: 84px; }
  .gl-form-header { padding: 16px 0 12px; }
  .gl-form-title { font-size: 18px; }

  .gl-staff-layout { grid-template-columns: 1fr !important; gap: 12px; }

  .gl-section { padding: 14px !important; }
  .gl-section-header { margin-bottom: 14px !important; gap: 10px; }
  .gl-section-icon { width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0; }
  .gl-section-title { font-size: 13.5px; }

  .gl-field-row { grid-template-columns: 1fr !important; gap: 12px; }
  .gl-fields { gap: 12px; }

  .gl-input { height: 46px !important; font-size: 15px !important; }
  .gl-select { height: 46px !important; font-size: 15px !important; }

  .gl-branch-cards { grid-template-columns: 1fr !important; gap: 8px; }
  .gl-branch-card { padding: 12px 14px; }

  .gl-form-actions { display: none !important; }
  .gl-sticky-bar { display: flex; }

  .gl-preview-strip { padding: 10px 14px; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-form-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-form-breadcrumb,
body.dark-only .gl-form-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-form-breadcrumb a:hover { color: #900C27 !important; }

body.dark-only .gl-preview-strip { background: #262932 !important; border-color: rgba(144,12,39,0.25) !important; }
body.dark-only .gl-preview-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-preview-tag { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-preview-status.inactive { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.35) !important; }

body.dark-only .gl-section { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-section-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-section-desc { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-label { color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-optional { color: rgba(255,255,255,0.25) !important; }

body.dark-only .gl-input {
  background: rgba(255,255,255,0.05) !important;
  border-color: #3d4255 !important;
  color: rgba(255,255,255,0.87) !important;
}
body.dark-only .gl-input:focus {
  background: rgba(255,255,255,0.07) !important;
  border-color: #900C27 !important;
  box-shadow: 0 0 0 3px rgba(144,12,39,0.15) !important;
}
body.dark-only .gl-input::placeholder { color: rgba(255,255,255,0.2) !important; }
body.dark-only .gl-pw-toggle { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-pw-toggle:hover { color: rgba(255,255,255,0.6) !important; }
body.dark-only .gl-pw-bar { background: rgba(255,255,255,0.08) !important; }
body.dark-only .gl-pw-bar.filled { background: #4caf97 !important; }

body.dark-only .gl-branch-card { background: rgba(255,255,255,0.04) !important; border-color: #3d4255 !important; }
body.dark-only .gl-branch-card:hover { background: rgba(144,12,39,0.10) !important; border-color: #900C27 !important; }
body.dark-only .gl-branch-card.active { background: rgba(144,12,39,0.12) !important; border-color: #900C27 !important; }
body.dark-only .gl-branch-card:not(.active) .gl-bcard-avatar { background: #3d4255 !important; color: rgba(255,255,255,0.4) !important; }
body.dark-only .gl-bcard-name { color: rgba(255,255,255,0.75) !important; }
body.dark-only .gl-branch-card.active .gl-bcard-name { color: #4caf97 !important; }
body.dark-only .gl-bcard-loc { color: rgba(255,255,255,0.3) !important; }

body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }

body.dark-only .gl-btn-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-btn-cancel:hover { background: #3d4255 !important; color: rgba(255,255,255,0.75) !important; }

body.dark-only .gl-sidebar-card { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-sidebar-card-header { color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-perm-list li { color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-perm-no { color: rgba(255,255,255,0.25) !important; }
body.dark-only .gl-perm-no svg { color: rgba(255,255,255,0.15) !important; }
body.dark-only .gl-perm-empty { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-assigned-item { color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-no-branches { background: rgba(251,191,36,0.08) !important; border-color: rgba(251,191,36,0.2) !important; color: #fbbf24 !important; }
body.dark-only .gl-sticky-bar { background: #1d1e26 !important; border-top-color: #323644 !important; box-shadow: 0 -4px 20px rgba(0,0,0,0.3) !important; }
</style>
